import { useState, useEffect, useRef } from "react";
import { api, API } from "../api";
import { Sparkles, FileText, MessageSquare, ClipboardCheck, Send, Trash2, Plus, Loader2 } from "lucide-react";

const TOOLS = [
  { key: "deal", label: "Deal Analyst", icon: Sparkles },
  { key: "triage", label: "Inquiry Triage", icon: ClipboardCheck },
  { key: "insight", label: "Insight Writer", icon: FileText },
  { key: "chat", label: "Chat", icon: MessageSquare },
];

export default function AIPanel({ inquiries }) {
  const [tool, setTool] = useState("deal");
  return (
    <div data-testid="ai-panel">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="serif-display" style={{ fontSize: 32 }}>GIANO Analytics</h1>
          <p className="text-sm mt-1" style={{ color: "#64748B" }}>
            Motore proprietario di analisi multi-modello · Team M&amp;A
          </p>
        </div>
      </div>

      <div className="flex gap-2 mb-6 flex-wrap" data-testid="ai-tabs">
        {TOOLS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTool(key)}
            data-testid={`ai-tab-${key}`}
            className="flex items-center gap-2 px-4 py-2 text-sm border transition-colors"
            style={{
              background: tool === key ? "#0F172A" : "#fff",
              color: tool === key ? "#fff" : "#0F172A",
              borderColor: tool === key ? "#0F172A" : "#E2E8F0",
            }}
          >
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {tool === "deal" && <DealAnalyst />}
      {tool === "triage" && <InquiryTriage inquiries={inquiries} />}
      {tool === "insight" && <InsightWriter />}
      {tool === "chat" && <ChatConsole />}
    </div>
  );
}

/* -------------------------- Deal Analyst -------------------------- */
function DealAnalyst() {
  const [form, setForm] = useState({
    company_name: "", sector: "", region: "", revenue_eur_m: "",
    ebitda_eur_m: "", ebitda_margin_pct: "", employees: "",
    year_founded: "", key_customers: "", key_risks: "",
    net_debt_eur_m: "", notes: "",
  });
  const [council, setCouncil] = useState(true);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const run = async () => {
    setLoading(true); setError(""); setResult(null);
    try {
      const payload = { ...form };
      ["revenue_eur_m", "ebitda_eur_m", "ebitda_margin_pct", "net_debt_eur_m"].forEach(k => {
        payload[k] = payload[k] === "" ? undefined : parseFloat(payload[k]);
      });
      ["employees", "year_founded"].forEach(k => {
        payload[k] = payload[k] === "" ? undefined : parseInt(payload[k]);
      });
      Object.keys(payload).forEach(k => { if (payload[k] === "" || payload[k] === undefined) delete payload[k]; });
      const { data } = await api.post(`/ai/deal-analyst?council=${council}`, payload, { timeout: 240000 });
      setResult(data.result);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="admin-card p-6">
        <h3 className="serif-display mb-4" style={{ fontSize: 22 }}>Input Target</h3>
        <div className="grid grid-cols-2 gap-3">
          {[
            ["company_name", "Nome azienda *", "text", 2],
            ["sector", "Settore", "text"],
            ["region", "Regione", "text"],
            ["revenue_eur_m", "Ricavi €M", "number"],
            ["ebitda_eur_m", "EBITDA €M", "number"],
            ["ebitda_margin_pct", "Margine EBITDA %", "number"],
            ["employees", "Dipendenti", "number"],
            ["year_founded", "Anno fondazione", "number"],
            ["net_debt_eur_m", "PFN €M", "number"],
            ["key_customers", "Clienti chiave", "text", 2],
            ["key_risks", "Rischi noti", "text", 2],
            ["notes", "Note", "textarea", 2],
          ].map(([k, label, type, span]) => (
            <div key={k} className={span === 2 ? "col-span-2" : ""}>
              <div className="overline mb-1" style={{ color: "#64748B" }}>{label}</div>
              {type === "textarea" ? (
                <textarea rows={3} className="admin-input" data-testid={`deal-${k}`}
                          value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
              ) : (
                <input type={type} step={type === "number" ? "0.1" : undefined}
                       className="admin-input" data-testid={`deal-${k}`}
                       value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
              )}
            </div>
          ))}
        </div>
        <button className="btn-primary mt-5" onClick={run} disabled={loading || !form.company_name} data-testid="deal-run">
          {loading ? <><Loader2 size={14} className="animate-spin" /> {council ? "Consensus del Team in corso…" : "Analisi in corso…"}</> : <><Sparkles size={14} /> Avvia analisi</>}
        </button>
        <label className="flex items-center gap-2 mt-3 text-xs cursor-pointer" style={{ color: "#64748B" }}>
          <input type="checkbox" checked={council} onChange={(e) => setCouncil(e.target.checked)} data-testid="deal-council-toggle" />
          <span>
            <strong>Modalità Consensus</strong> — tre analisti senior del Team M&amp;A valutano in parallelo e sintetizzano il verdetto (consigliata per deal &gt;€2M).
          </span>
        </label>
        {error && <div className="text-sm mt-3" style={{ color: "#B91C1C" }} data-testid="deal-error">{error}</div>}
      </div>

      <div className="admin-card p-6" data-testid="deal-result">
        <h3 className="serif-display mb-4" style={{ fontSize: 22 }}>Report</h3>
        {!result && !loading && <p className="text-sm" style={{ color: "#64748B" }}>Il report apparirà qui.</p>}
        {loading && (
          <div className="flex items-center gap-2 text-sm" style={{ color: "#64748B" }}>
            <Loader2 size={16} className="animate-spin" /> Il Team M&amp;A sta analizzando il target…
          </div>
        )}
        {result && <DealReport r={result} />}
      </div>
    </div>
  );
}

function DealReport({ r }) {
  if (r.raw) {
    return <pre className="text-xs whitespace-pre-wrap" style={{ color: "#334155" }}>{r.raw}</pre>;
  }
  const verdictColor = r.verdict === "PROCEED" ? "#065F46" : r.verdict === "PASS" ? "#B91C1C" : "#92400E";
  const isCouncil = r._mode === "council";
  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="chip font-mono" style={{ background: verdictColor, color: "#fff", borderColor: verdictColor }}>
          {r.verdict}
        </span>
        <span className="font-mono text-xs" style={{ color: "#64748B" }}>Confidence: {r.confidence}</span>
        {isCouncil && (
          <span className="chip font-mono" style={{ background: "#C4A676", color: "#0F1720", borderColor: "#C4A676" }}
                title="Analisi consensuale dei tre analisti senior del Team M&A">
            CONSENSUS · Council
          </span>
        )}
      </div>
      <p className="text-sm leading-relaxed" style={{ color: "#0F172A" }}>{r.summary_it}</p>

      {r.consensus_notes_it && (
        <div className="p-3 border" style={{ borderColor: "#C4A676", background: "#FFFBEB" }}>
          <div className="overline mb-1" style={{ color: "#92400E" }}>Note del Consensus</div>
          <p className="text-sm italic">{r.consensus_notes_it}</p>
        </div>
      )}

      {r._members && r._members.length > 0 && (
        <details data-testid="council-members" open>
          <summary className="cursor-pointer overline" style={{ color: "#64748B" }}>
            Vedi le 3 opinioni indipendenti degli analisti ({r._members.length})
          </summary>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
            {r._members.map((m) => (
              <div key={m.index} className="p-3 border" style={{ borderColor: "#E2E8F0", background: "#F8FAFC" }}>
                <div className="overline mb-2" style={{ color: "#64748B" }}>Analista {m.index}</div>
                {m.opinion.raw ? (
                  <pre className="text-xs whitespace-pre-wrap" style={{ maxHeight: 200, overflowY: "auto" }}>{m.opinion.raw.slice(0, 300)}…</pre>
                ) : (
                  <div className="text-xs space-y-1">
                    <div><strong>Verdict:</strong> {m.opinion.verdict || "—"}</div>
                    {m.opinion.multiple_range && (
                      <div><strong>Multiplo:</strong> {m.opinion.multiple_range.low}–{m.opinion.multiple_range.high}×</div>
                    )}
                    {m.opinion.offer_price_range_eur_m && (
                      <div><strong>Prezzo:</strong> €{m.opinion.offer_price_range_eur_m.low}–{m.opinion.offer_price_range_eur_m.high}M</div>
                    )}
                    <div><strong>Red flags:</strong> {(m.opinion.red_flags || []).length}</div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </details>
      )}

      {r.offer_price_range_eur_m && (
        <div className="grid grid-cols-3 gap-3">
          <Kpi label="Prezzo min" value={`€${r.offer_price_range_eur_m.low}M`} />
          <Kpi label="Prezzo max" value={`€${r.offer_price_range_eur_m.high}M`} />
          <Kpi label="Multiplo" value={`${r.multiple_range?.low}–${r.multiple_range?.high}x`} />
        </div>
      )}
      {r.structure_suggestion && (
        <div>
          <div className="overline mb-2" style={{ color: "#64748B" }}>Struttura prezzo suggerita</div>
          <div className="flex gap-2 font-mono text-xs">
            <Pill label={`Cash ${r.structure_suggestion.cash_pct}%`} />
            <Pill label={`Earn-out ${r.structure_suggestion.earnout_pct}%`} />
            <Pill label={`Vendor loan ${r.structure_suggestion.vendor_loan_pct}%`} />
          </div>
        </div>
      )}
      {r.red_flags?.length > 0 && (
        <div>
          <div className="overline mb-2" style={{ color: "#B91C1C" }}>Red flags</div>
          <ul className="space-y-2 text-sm">
            {r.red_flags.map((f, i) => (
              <li key={i} className="p-3 border" style={{ borderColor: "#E2E8F0", background: "#FEF2F2" }}>
                <span className="chip" style={{ borderColor: "#B91C1C", color: "#B91C1C", marginRight: 8 }}>{f.severity}</span>
                <strong>{f.issue}</strong> — <span style={{ color: "#64748B" }}>{f.impact}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {r.strengths?.length > 0 && (
        <div>
          <div className="overline mb-2" style={{ color: "#065F46" }}>Punti di forza</div>
          <ul className="list-disc pl-5 text-sm space-y-1">
            {r.strengths.map((s, i) => <li key={i}>{s}</li>)}
          </ul>
        </div>
      )}
      {r.questions_for_seller?.length > 0 && (
        <div>
          <div className="overline mb-2" style={{ color: "#64748B" }}>Domande per il venditore</div>
          <ul className="list-disc pl-5 text-sm space-y-1">
            {r.questions_for_seller.map((q, i) => <li key={i}>{q}</li>)}
          </ul>
        </div>
      )}
      {r.cultural_fit && (
        <div>
          <div className="overline mb-2" style={{ color: "#64748B" }}>Cultural fit</div>
          <p className="text-sm">{r.cultural_fit}</p>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value }) {
  return (
    <div className="p-3 border" style={{ borderColor: "#E2E8F0" }}>
      <div className="overline" style={{ color: "#64748B" }}>{label}</div>
      <div className="serif-display mt-1" style={{ fontSize: 22 }}>{value}</div>
    </div>
  );
}

function Pill({ label }) {
  return <span className="chip" style={{ background: "#F1F5F9" }}>{label}</span>;
}

/* -------------------------- Inquiry Triage -------------------------- */
function InquiryTriage({ inquiries }) {
  const [running, setRunning] = useState(null);
  const [results, setResults] = useState({});
  const [error, setError] = useState("");

  const triage = async (id) => {
    setRunning(id); setError("");
    try {
      const { data } = await api.post(`/ai/inquiry-triage/${id}`, {}, { timeout: 120000 });
      setResults((r) => ({ ...r, [id]: data.result }));
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setRunning(null);
    }
  };

  return (
    <div>
      <p className="text-sm mb-4" style={{ color: "#64748B" }}>
        Per ogni richiesta ricevuta, il Team M&amp;A produce priorità, sintesi, next best action e bozza di risposta email.
      </p>
      {error && <div className="text-sm mb-3" style={{ color: "#B91C1C" }}>{error}</div>}
      <div className="space-y-4">
        {inquiries.length === 0 && (
          <div className="admin-card p-8 text-center" style={{ color: "#64748B" }}>
            Nessuna richiesta da valutare. Le richieste dal form pubblico appariranno qui.
          </div>
        )}
        {inquiries.map((iq) => {
          const r = results[iq.id] || iq.ai_triage;
          return (
            <div key={iq.id} className="admin-card p-5" data-testid={`triage-item-${iq.id}`}>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="serif-display" style={{ fontSize: 20 }}>{iq.company}</div>
                  <div className="text-xs mt-1" style={{ color: "#64748B" }}>
                    {iq.full_name} · {iq.email} · <span className="font-mono">{iq.revenue_range || "—"}</span> · {iq.region || "—"}
                  </div>
                </div>
                <button className="btn-ghost" style={{ padding: "0.5rem 1rem", fontSize: 13 }}
                        onClick={() => triage(iq.id)} disabled={running === iq.id}
                        data-testid={`triage-run-${iq.id}`}>
                  {running === iq.id ? <><Loader2 size={14} className="animate-spin" /> Analisi…</> : <><Sparkles size={14} /> {r ? "Rianalizza" : "Triage AI"}</>}
                </button>
              </div>
              <p className="text-sm p-3 border" style={{ borderColor: "#E2E8F0", background: "#F8FAFC" }}>
                {iq.message}
              </p>

              {r && !r.raw && (
                <div className="mt-4 space-y-3">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="chip font-mono" style={{
                      background: r.priority_label === "P0" ? "#B91C1C" : r.priority_label === "P1" ? "#EA580C" : r.priority_label === "P2" ? "#CA8A04" : "#64748B",
                      color: "#fff", borderColor: "transparent"
                    }}>
                      {r.priority_label} · Score {r.priority_score}
                    </span>
                    {r.estimated_ebitda_range_eur_m && (r.estimated_ebitda_range_eur_m.low != null) && (
                      <span className="font-mono text-xs" style={{ color: "#64748B" }}>
                        EBITDA stim: €{r.estimated_ebitda_range_eur_m.low}–{r.estimated_ebitda_range_eur_m.high}M
                      </span>
                    )}
                  </div>
                  <p className="text-sm"><strong>Sintesi:</strong> {r.summary_it}</p>
                  <p className="text-sm"><strong>Next best action:</strong> {r.next_best_action}</p>
                  {r.risk_flags?.length > 0 && (
                    <div className="text-sm">
                      <strong>Risk flags:</strong>
                      <ul className="list-disc pl-5 mt-1">{r.risk_flags.map((x, i) => <li key={i}>{x}</li>)}</ul>
                    </div>
                  )}
                  {r.suggested_response_it && (
                    <div>
                      <div className="overline mb-2" style={{ color: "#64748B" }}>Bozza risposta (copiabile)</div>
                      <textarea rows={5} className="admin-input font-mono text-xs" readOnly value={r.suggested_response_it} />
                    </div>
                  )}
                </div>
              )}
              {r?.raw && <pre className="text-xs mt-3 whitespace-pre-wrap">{r.raw}</pre>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* -------------------------- Insight Writer -------------------------- */
function InsightWriter() {
  const [brief, setBrief] = useState({ topic: "", angle: "", category: "insight", target_length_words: 600 });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  const generate = async () => {
    setLoading(true); setError(""); setResult(null); setSaved(false);
    try {
      const { data } = await api.post("/ai/insight-writer", brief, { timeout: 180000 });
      setResult(data.result);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };

  const publish = async () => {
    if (!result) return;
    try {
      const payload = {
        slug: result.slug, category: result.category || "insight",
        title_it: result.title_it, title_en: result.title_en,
        excerpt_it: result.excerpt_it, excerpt_en: result.excerpt_en,
        body_it: result.body_it, body_en: result.body_en,
        cover_url: "https://images.pexels.com/photos/36373012/pexels-photo-36373012.jpeg",
      };
      await api.post("/admin/insights", payload);
      setSaved(true);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="admin-card p-6">
        <h3 className="serif-display mb-4" style={{ fontSize: 22 }}>Brief</h3>
        <div className="space-y-3">
          <div>
            <div className="overline mb-1" style={{ color: "#64748B" }}>Tema *</div>
            <input className="admin-input" data-testid="insight-topic"
                   placeholder="es. Perché i multipli PMI italiane restano sotto la media UE"
                   value={brief.topic} onChange={(e) => setBrief({ ...brief, topic: e.target.value })} />
          </div>
          <div>
            <div className="overline mb-1" style={{ color: "#64748B" }}>Angolo (opzionale)</div>
            <input className="admin-input"
                   placeholder="Prospettiva GIANO, dati, controintuizione…"
                   value={brief.angle} onChange={(e) => setBrief({ ...brief, angle: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="overline mb-1" style={{ color: "#64748B" }}>Categoria</div>
              <select className="admin-input" value={brief.category}
                      onChange={(e) => setBrief({ ...brief, category: e.target.value })}>
                <option value="insight">insight</option>
                <option value="case-study">case-study</option>
                <option value="press">press</option>
              </select>
            </div>
            <div>
              <div className="overline mb-1" style={{ color: "#64748B" }}>Lunghezza (parole)</div>
              <input type="number" className="admin-input" value={brief.target_length_words}
                     onChange={(e) => setBrief({ ...brief, target_length_words: parseInt(e.target.value) || 600 })} />
            </div>
          </div>
          <button className="btn-primary" onClick={generate} disabled={loading || !brief.topic} data-testid="insight-generate">
            {loading ? <><Loader2 size={14} className="animate-spin" /> Genero…</> : <><Sparkles size={14} /> Genera bozza</>}
          </button>
          {error && <div className="text-sm" style={{ color: "#B91C1C" }}>{error}</div>}
        </div>
      </div>

      <div className="admin-card p-6" data-testid="insight-result">
        <h3 className="serif-display mb-4" style={{ fontSize: 22 }}>Bozza</h3>
        {!result && !loading && <p className="text-sm" style={{ color: "#64748B" }}>La bozza apparirà qui.</p>}
        {loading && <div className="flex items-center gap-2 text-sm" style={{ color: "#64748B" }}><Loader2 size={16} className="animate-spin" /> Il Team M&A sta scrivendo…</div>}
        {result && !result.raw && (
          <div className="space-y-4">
            <div><div className="overline" style={{ color: "#64748B" }}>Slug</div><div className="font-mono text-xs">{result.slug}</div></div>
            <div><div className="overline" style={{ color: "#64748B" }}>Titolo IT</div><div className="serif-display" style={{ fontSize: 22 }}>{result.title_it}</div></div>
            <div><div className="overline" style={{ color: "#64748B" }}>Excerpt IT</div><p className="text-sm italic">{result.excerpt_it}</p></div>
            <div><div className="overline" style={{ color: "#64748B" }}>Body IT</div><p className="text-sm whitespace-pre-wrap leading-relaxed">{result.body_it}</p></div>
            <details><summary className="text-sm cursor-pointer" style={{ color: "#64748B" }}>Vedi versione EN</summary>
              <div className="mt-3"><strong>{result.title_en}</strong><p className="text-sm italic mt-2">{result.excerpt_en}</p><p className="text-sm mt-3 whitespace-pre-wrap">{result.body_en}</p></div>
            </details>
            <button onClick={publish} className="btn-primary" data-testid="insight-publish">
              Pubblica nel sito
            </button>
            {saved && <div className="text-sm" style={{ color: "#065F46" }}>Pubblicato ✓</div>}
          </div>
        )}
        {result?.raw && <pre className="text-xs whitespace-pre-wrap">{result.raw}</pre>}
      </div>
    </div>
  );
}

/* -------------------------- Chat Console -------------------------- */
function ChatConsole() {
  const [sessions, setSessions] = useState([]);
  const [current, setCurrent] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [assistantBuf, setAssistantBuf] = useState("");
  const bottomRef = useRef(null);

  const loadSessions = async () => {
    const { data } = await api.get("/ai/chat/sessions");
    setSessions(data);
    if (!current && data.length > 0) selectSession(data[0]);
  };

  const selectSession = async (s) => {
    setCurrent(s);
    const { data } = await api.get(`/ai/chat/sessions/${s.id}/messages`);
    setMessages(data);
  };

  const newSession = async () => {
    const { data } = await api.post("/ai/chat/sessions", { title: "Nuova conversazione" });
    setSessions((prev) => [data, ...prev]);
    setCurrent(data); setMessages([]);
  };

  const deleteSession = async (id) => {
    if (!window.confirm("Eliminare la conversazione?")) return;
    await api.delete(`/ai/chat/sessions/${id}`);
    setSessions((prev) => prev.filter((s) => s.id !== id));
    if (current?.id === id) { setCurrent(null); setMessages([]); }
  };

  useEffect(() => { loadSessions(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, assistantBuf]);

  const send = async () => {
    if (!input.trim() || streaming) return;
    let sess = current;
    if (!sess) {
      const { data } = await api.post("/ai/chat/sessions", { title: input.slice(0, 40) });
      sess = data;
      setSessions((prev) => [data, ...prev]);
      setCurrent(data);
    }
    const userText = input;
    setInput("");
    setMessages((prev) => [...prev, { id: "tmp-u", role: "user", content: userText, created_at: new Date().toISOString() }]);
    setStreaming(true); setAssistantBuf("");

    const token = localStorage.getItem("giano_token");
    try {
      const resp = await fetch(`${API}/ai/chat/sessions/${sess.id}/message`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({ content: userText }),
      });
      if (!resp.ok || !resp.body) {
        setStreaming(false);
        setMessages((prev) => [...prev, { id: "tmp-err", role: "assistant", content: `Errore ${resp.status}`, created_at: new Date().toISOString() }]);
        return;
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop() || "";
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          try {
            const data = JSON.parse(line.slice(5).trim());
            if (data.delta) { acc += data.delta; setAssistantBuf(acc); }
            if (data.done) { /* handled below */ }
            if (data.error) { acc += `\n[error: ${data.error}]`; setAssistantBuf(acc); }
          } catch { /* ignore */ }
        }
      }
      setMessages((prev) => [...prev, { id: "tmp-a", role: "assistant", content: acc, created_at: new Date().toISOString() }]);
      setAssistantBuf("");
    } catch (e) {
      setMessages((prev) => [...prev, { id: "tmp-err", role: "assistant", content: `Errore: ${e.message}`, created_at: new Date().toISOString() }]);
    } finally {
      setStreaming(false);
      loadSessions();
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6" style={{ minHeight: 600 }}>
      {/* sidebar */}
      <div className="admin-card p-4 lg:col-span-1">
        <button onClick={newSession} className="btn-primary w-full justify-center mb-3" data-testid="chat-new">
          <Plus size={14} /> Nuova
        </button>
        <div className="space-y-1 max-h-[540px] overflow-y-auto">
          {sessions.map((s) => (
            <div key={s.id}
                 onClick={() => selectSession(s)}
                 className="p-2 cursor-pointer flex items-center justify-between group"
                 style={{
                   background: current?.id === s.id ? "#F1F5F9" : "transparent",
                   borderLeft: current?.id === s.id ? "2px solid #0F172A" : "2px solid transparent",
                 }}
                 data-testid={`chat-session-${s.id}`}>
              <div className="text-sm truncate">{s.title}</div>
              <button onClick={(e) => { e.stopPropagation(); deleteSession(s.id); }}
                      className="opacity-0 group-hover:opacity-100 transition-opacity">
                <Trash2 size={13} />
              </button>
            </div>
          ))}
          {sessions.length === 0 && <div className="text-xs" style={{ color: "#64748B" }}>Nessuna conversazione</div>}
        </div>
      </div>

      {/* thread */}
      <div className="admin-card lg:col-span-3 flex flex-col" style={{ minHeight: 600 }}>
        <div className="flex-1 overflow-y-auto p-6 space-y-4" style={{ maxHeight: 540 }}>
          {messages.length === 0 && !streaming && (
            <div className="text-center" style={{ color: "#64748B", paddingTop: 100 }}>
              <MessageSquare size={36} style={{ opacity: 0.4, margin: "0 auto" }} />
              <p className="mt-3 text-sm">Chiedi al Team M&A di analizzare un target, spiegare un multiplo,<br />bozzare una lettera all'imprenditore, o brainstormare nicchie difendibili.</p>
            </div>
          )}
          {messages.map((m) => (
            <div key={m.id} data-testid={`chat-msg-${m.role}`}
                 className="max-w-[80%]"
                 style={{ marginLeft: m.role === "user" ? "auto" : 0 }}>
              <div className="overline mb-1" style={{ color: "#64748B" }}>
                {m.role === "user" ? "Tu" : "Team M&A"}
              </div>
              <div className="p-3 border text-sm whitespace-pre-wrap leading-relaxed"
                   style={{
                     background: m.role === "user" ? "#0F172A" : "#fff",
                     color: m.role === "user" ? "#F8FAFC" : "#0F172A",
                     borderColor: m.role === "user" ? "#0F172A" : "#E2E8F0",
                   }}>
                {m.content}
              </div>
            </div>
          ))}
          {streaming && assistantBuf && (
            <div className="max-w-[80%]">
              <div className="overline mb-1" style={{ color: "#64748B" }}>Team M&A</div>
              <div className="p-3 border text-sm whitespace-pre-wrap leading-relaxed" style={{ background: "#fff", borderColor: "#E2E8F0" }}>
                {assistantBuf}<span className="animate-pulse">▊</span>
              </div>
            </div>
          )}
          {streaming && !assistantBuf && (
            <div className="flex items-center gap-2 text-sm" style={{ color: "#64748B" }}>
              <Loader2 size={14} className="animate-spin" /> Il Team M&A sta ragionando…
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        <div className="border-t p-4 flex gap-2" style={{ borderColor: "#E2E8F0" }}>
          <input className="admin-input flex-1" data-testid="chat-input"
                 placeholder="Scrivi un messaggio…" value={input}
                 onChange={(e) => setInput(e.target.value)}
                 onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                 disabled={streaming} />
          <button className="btn-primary" onClick={send} disabled={streaming || !input.trim()} data-testid="chat-send">
            <Send size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
